import { useMemo } from 'react';
import {
  Autocomplete,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAdminScenariosQuery, useScenariosQuery } from '../../api/gql';
import { useControlledTableModel } from '../../hooks/useControlledTableModel';

const TRANS_NS = 'page-upload';

type Props = {
  onScenarioSelected: (scenarioId: string) => void;
};

export const ScenarioSelectInputInfoBox = (props: Props) => {
  const { t } = useTranslation(TRANS_NS);
  return (
    <Paper
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: 1024,
        gap: 4,
      }}
    >
      <Box>
        <Typography variant={'h3'}>{t('scenarios.info.title')}</Typography>
        <Typography variant={'body2'} color={'text.secondary'} whiteSpace={'break-spaces'}>
          {t('scenarios.info.description')}
        </Typography>
      </Box>
      <ScenariosSelectInput onScenarioSelected={props.onScenarioSelected} />
    </Paper>
  );
};

export const ScenariosSelectInput = ({ onScenarioSelected }: Props) => {
  const { t } = useTranslation(TRANS_NS);
  const { data } = useScenariosQuery();

  const scenarios = data?.scenarios.filter((s) => !s.static) ?? [];

  return (
    <FormControl fullWidth>
      <InputLabel
        id={`scenario-select-label`}
        sx={{
          bgcolor: 'white',
        }}
      >
        {t('scenarios.title')}
      </InputLabel>
      <Select<string>
        id={`scenario-select`}
        labelId={`scenario-select-label`}
        defaultValue={'default'}
        onChange={(e) => {
          const v = e.target.value;
          onScenarioSelected(v === 'default' ? '' : v);
        }}
      >
        <MenuItem value={'default'}>{t('scenarios.default')}</MenuItem>

        {scenarios.map(({ id, name }) => (
          <MenuItem key={id} value={id}>
            {name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export const AdminScenariosAutocomplete = ({ onScenarioSelected }: Props) => {
  const { t } = useTranslation(TRANS_NS);

  const { debouncedQuery, handlers } = useControlledTableModel({
    pageSize: 25,
  });
  const { data } = useAdminScenariosQuery({
    query: debouncedQuery,
    skip: 0,
    take: 25,
  });

  const options = useMemo(
    () =>
      data?.adminScenarios.results
        .filter((s) => !s.static)
        .map((scenario) => ({
          id: scenario.id,
          label: scenario.name,
        })) ?? [],
    [data],
  );

  return (
    <FormControl fullWidth>
      <Autocomplete
        renderInput={(params) => <TextField {...params} label={t('scenarios.title')} fullWidth />}
        options={options}
        onInputChange={(e, value) => handlers.setQuery(value)}
        onChange={(_, value) => {
          if (value?.id) {
            onScenarioSelected(value?.id);
          }
        }}
        isOptionEqualToValue={(option, value) => {
          if (typeof value === 'string') {
            return option.id === value;
          }
          return option.id === value.id;
        }}
      />
    </FormControl>
  );
};
